import { useState } from 'react';
import type { GameSettings } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import type { TranslationKey } from '../locales.ts';
import { IconChevron } from './Icon.tsx';

/**
 * Regel-Einstellungen des Hosts.
 *
 * Die Standardwerte entsprechen dem Original-Hitster (10 Karten,
 * 3 Token, Klauen und Überspringen erlaubt).
 *
 * Aufbau: die drei Dinge, die man wirklich oft ändert, stehen oben und
 * nebeneinander. Alles Weitere liegt hinter „Mehr" — vorher war die
 * Liste so lang, dass man für den Start-Knopf scrollen musste.
 */
export function SettingsPanel({
  settings,
  disabled,
  onChange,
}: {
  settings: GameSettings;
  disabled: boolean;
  onChange: (patch: Partial<GameSettings>) => void;
}) {
  const t = useT();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const numberField = (
    key: keyof GameSettings,
    labelKey: TranslationKey,
    min: number,
    max: number,
    hintKey?: TranslationKey,
  ) => (
    <div className="field">
      <label className="field__label" htmlFor={`set-${key}`}>
        {t(labelKey)}
      </label>
      <input
        id={`set-${key}`}
        className="input input--number"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        disabled={disabled}
        value={String(settings[key])}
        onChange={(event) => {
          const value = Number(event.target.value);
          if (!Number.isFinite(value)) return;
          onChange({ [key]: Math.min(max, Math.max(min, value)) } as Partial<GameSettings>);
        }}
      />
      {hintKey && <span className="field__hint">{t(hintKey)}</span>}
    </div>
  );

  const toggle = (key: keyof GameSettings, labelKey: TranslationKey, hintKey?: TranslationKey) => (
    <div>
      <label className="switch">
        <input
          type="checkbox"
          disabled={disabled}
          checked={Boolean(settings[key])}
          onChange={(event) => onChange({ [key]: event.target.checked } as Partial<GameSettings>)}
        />
        <span>{t(labelKey)}</span>
      </label>
      {hintKey && <span className="field__hint">{t(hintKey)}</span>}
    </div>
  );

  return (
    <div className="panel">
      <div className="panel__title">{t('settings.title')}</div>

      {disabled && <p className="text-sm text-dim">{t('settings.lockedDuringGame')}</p>}

      {/* --- Das Wesentliche, nebeneinander --- */}
      <div className="settings-grid">
        {numberField('targetCards', 'settings.targetCards', 3, 30)}
        {numberField('maxTokens', 'settings.maxTokens', 0, 10)}
      </div>

      <div className="settings-grid" style={{ marginTop: '0.5rem' }}>
        {toggle('allowSteal', 'settings.allowSteal')}
        {toggle('allowSkip', 'settings.allowSkip')}
        {toggle('allowTitleArtistGuess', 'settings.allowGuess')}
        {toggle('requireVerifiedYear', 'settings.verifiedYear')}
      </div>

      {/* Nur sinnvoll, wenn ueberhaupt geraten wird. */}
      {settings.allowTitleArtistGuess && (
        <div className="settings-grid">
          {toggle('guessMultipleChoice', 'settings.guessChoice', 'settings.guessChoiceHint')}
        </div>
      )}

      <button
        type="button"
        className="btn btn--ghost btn--block"
        onClick={() => setShowAdvanced((previous) => !previous)}
        aria-expanded={showAdvanced}
        style={{ marginTop: '0.5rem' }}
      >
        <IconChevron up={showAdvanced} />
        {t('settings.more')}
      </button>

      {showAdvanced && (
        <div className="stack stack--tight" style={{ marginTop: '0.5rem' }}>
          <span className="field__hint">{t('settings.allowStealHint')}</span>
          <span className="field__hint">{t('settings.allowGuessHint')}</span>
          <span className="field__hint">{t('settings.verifiedYearHint')}</span>

          <div className="settings-grid">
            {settings.allowSteal && numberField('stealWindowSec', 'settings.stealWindow', 3, 120)}
            {numberField('placementTimeLimitSec', 'settings.placementTime', 0, 300)}

            {/* Intern in Millisekunden, in der UI in Sekunden. */}
            <div className="field">
              <label className="field__label" htmlFor="set-startAt">
                {t('settings.startAt')}
              </label>
              <input
                id="set-startAt"
                className="input input--number"
                type="number"
                inputMode="numeric"
                min={0}
                max={120}
                disabled={disabled}
                value={String(Math.round(settings.startAtMs / 1000))}
                onChange={(event) => {
                  const seconds = Number(event.target.value);
                  if (!Number.isFinite(seconds)) return;
                  onChange({ startAtMs: Math.min(120, Math.max(0, seconds)) * 1000 });
                }}
              />
              <span className="field__hint">{t('settings.startAtHint')}</span>
            </div>

            {settings.autoAdvance &&
              numberField('autoAdvanceDelaySec', 'settings.autoAdvanceDelay', 3, 60)}
          </div>

          {toggle('autoAdvance', 'settings.autoAdvance', 'settings.autoAdvanceHint')}
        </div>
      )}
    </div>
  );
}
